/**
 * Shared Login component for all portals.
 * Physically separated routing paths per role.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

const Login = ({ role = 'patient' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [switchingOut, setSwitchingOut] = useState(false);
  const { login, resetPassword, currentUser, userRole, logout } = useAuth();
  const navigate = useNavigate();

  const roleToPortal = {
    patient: 'patient',
    doctor: 'doctor',
    pending_doctor: 'doctor',
    admin: 'admin',
  };
  // True when a different-role user is viewing this login page
  const isWrongPortal = currentUser && userRole && roleToPortal[userRole] !== role;

  const handleSwitchAccount = async () => {
    setSwitchingOut(true);
    try {
      await logout();
    } finally {
      setSwitchingOut(false);
    }
  };

  const roleLabelMap = {
    patient: 'Patient',
    doctor: 'Doctor',
    pending_doctor: 'Doctor (pending)',
    admin: 'Admin',
  };
  const currentRoleLabel = roleLabelMap[userRole] || userRole;

  const roleConfig = {
    patient: {
      title: 'Patient Login',
      subtitle: 'Access your health dashboard',
      icon: '🧑‍⚕️',
      color: 'primary',
      registerLink: '/patient/register',
      dashboardLink: '/patient/dashboard',
      bgGradient: 'from-primary-50 to-blue-50',
    },
    doctor: {
      title: 'Doctor Login',
      subtitle: 'Manage your practice',
      icon: '👨‍⚕️',
      color: 'accent',
      registerLink: '/doctor/register',
      dashboardLink: '/doctor/dashboard',
      bgGradient: 'from-accent-50 to-green-50',
    },
    admin: {
      title: 'Admin Login',
      subtitle: 'System administration',
      icon: '🔐',
      color: 'primary',
      registerLink: null,
      dashboardLink: '/admin/dashboard',
      bgGradient: 'from-gray-50 to-slate-100',
    },
  };

  const config = roleConfig[role];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      
      // Verify the user has the correct role from Firebase custom claims
      let tokenResult = await user.getIdTokenResult();
      let userRole = tokenResult.claims.role;

      if (!userRole) {
        try {
          const idToken = await user.getIdToken(true);
          const verifyResponse = await authAPI.verifyToken(idToken);
          userRole = verifyResponse?.data?.role || null;

          if (userRole) {
            await user.getIdToken(true);
            tokenResult = await user.getIdTokenResult();
            userRole = tokenResult.claims.role || userRole;
          }
        } catch {
          // No profile yet or token verification failed - handled below.
        }
      }
      
      // Block login if no role has been assigned (registration incomplete)
      if (!userRole) {
        toast.error('Account setup incomplete. Please register first.');
        await signOut(auth);
        setLoading(false);
        return;
      }

      // Role-based access control — use GENERIC error to prevent role enumeration
      if (role === 'admin' && userRole !== 'admin') {
        toast.error('Invalid credentials for this portal.');
        await signOut(auth);
        setLoading(false);
        return;
      }
      
      if (role === 'doctor' && userRole !== 'doctor' && userRole !== 'pending_doctor') {
        toast.error('Invalid credentials for this portal.');
        await signOut(auth);
        setLoading(false);
        return;
      }

      if (role === 'patient' && userRole !== 'patient') {
        toast.error('Invalid credentials for this portal.');
        await signOut(auth);
        setLoading(false);
        return;
      }

      toast.success('Welcome back!');
      
      if (userRole === 'pending_doctor') {
        navigate('/doctor/pending');
      } else {
        navigate(config.dashboardLink);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Use generic error messages to prevent account enumeration
      const sensitiveErrors = [
        'auth/user-not-found',
        'auth/wrong-password',
        'auth/invalid-credential'
      ];
      
      if (sensitiveErrors.includes(err.code)) {
        toast.error('Invalid email or password.');
      } else if (err.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again later.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Enter your email address first.');
      return;
    }
    try {
      await resetPassword(email);
      toast.success('Password reset email sent!');
      setShowReset(false);
    } catch (err) {
      toast.error('Could not send reset email. Check your email address.');
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${config.bgGradient}`}>
      <div className="max-w-md mx-auto px-6 pt-12 pb-8">
        {/* Back to landing */}
        <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-8">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>

        {/* Switch-account banner — shown when a different-role user visits this portal */}
        {isWrongPortal && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-sm font-medium text-amber-800">
              You're currently signed in as a <span className="font-bold">{currentRoleLabel}</span>.
            </p>
            <p className="text-xs text-amber-600 mt-1 mb-3">
              Sign out first to log into the {role} portal.
            </p>
            <button
              onClick={handleSwitchAccount}
              disabled={switchingOut}
              className="w-full py-2 px-4 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-60"
            >
              {switchingOut ? 'Signing out...' : 'Sign out & switch account'}
            </button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-sm mx-auto mb-4 flex items-center justify-center">
            <span className="text-3xl">{config.icon}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{config.title}</h1>
          <p className="text-gray-500 mt-1">{config.subtitle}</p>
        </div>

        {/* Login Form */}
        <div className="card">
          <form onSubmit={showReset ? handlePasswordReset : handleLogin}>
            <div className="mb-4">
              <label className="label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            {!showReset && (
              <div className="mb-6">
                <label className="label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  minLength={6}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading && <div className="spinner w-5 h-5 border-2"></div>}
              {showReset ? 'Send Reset Link' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => setShowReset(!showReset)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {showReset ? 'Back to login' : 'Forgot password?'}
            </button>
          </div>
        </div>

        {/* Register Link */}
        {config.registerLink && (
          <p className="text-center mt-6 text-gray-600">
            Don't have an account?{' '}
            <Link to={config.registerLink} className="font-semibold text-primary-600 hover:text-primary-700">
              Register here
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
